import {
  SET_MAP_STYLE,
  TOGGLE_MAP_STYLE,
  MAP_STYLE_SELECTED_FEATURES_SET,
  MAP_STYLE_CLICKED_FEATURE_SET,
  MAP_STYLE_LAYER_FILTER_SET,
  MAP_STYLE_LAYER_FILTERS_SET,
  MAP_STYLE_LAYER_VISIBILITY_SET,
  MAP_STYLE_LAYERS_VISIBILITY_SET,
  MAP_STYLE_LAYERS_FILTER_AND_MODEL_SET,
  MAP_STYLE_LAYER_PAINT_SET,
  MAP_STYLE_LAYER_TYPE_SET,
  MAP_STYLE_SOURCE_DATA_SET,
  MAP_STYLE_SOURCE_SET,
  MAP_STYLE_SOURCES_EMPTY,
  MAP_STYLE_SOURCE_VERSION_BUMP,
  MAP_STYLE_LAYERS_REPLACE,
  MAP_STYLE_BUST_LAYER_TILES,
} from '@/actions/types'

import { styleToggleObject } from '@/mapStyles/style_config'
import { bustLayerTilesSurgical } from '@/utilities/mapStyle'
import type { MapStyleJS } from '@/utilities/mapStyleUtils'
import type { MapStyleAction } from '@/actions/actions_map_style'

type MapStyleState = MapStyleJS | null
type StyleLayer = Record<string, any> & { id?: string }

const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj))

const getOutlinesAndSymbolLayers = (payload: unknown): string[] => {
  if (!Array.isArray(payload)) return []
  const ids = payload
    .filter(Boolean)
    .map(id => [id, id + '_outline', id + '_symbol'])
  return ([] as string[]).concat(...ids)
}

const ensureLayerLayout = (layer: StyleLayer) => {
  if (!layer.layout || layer.layout.constructor !== Object) {
    layer.layout = {}
  }
  if (
    layer.layout.visibility !== 'visible' &&
    layer.layout.visibility !== 'none'
  ) {
    layer.layout.visibility = 'none'
  }
}

/**
 * Reference-preserving layer update: returns a new style where ONLY the matched
 * layer (and the `layers` array + root) is a fresh reference; every other layer
 * keeps its identity. This keeps MapLibre's `setStyle(diff:true)` cheap — see
 * `bustLayerTilesSurgical` in `@/utilities/mapStyle` for the full rationale.
 * Returns the input unchanged (same reference) when no layer matches, so an
 * unrelated dispatch can't trigger a no-op restyle.
 */
const updateLayerById = (
  state: MapStyleJS,
  layerId: string,
  updater: (layer: StyleLayer) => StyleLayer
): MapStyleJS => {
  if (!Array.isArray(state.layers)) return state
  let changed = false
  const layers = state.layers.map((layer: StyleLayer) => {
    if (!layer || layer.id !== layerId) return layer
    changed = true
    return updater(layer)
  })
  if (!changed) return state
  return { ...state, layers }
}

/** Reference-preserving source `.data` set (GeoJSON sources). */
const setSourceData = (
  state: MapStyleJS,
  sourceId: string,
  data: unknown
): MapStyleJS => {
  const source = state.sources?.[sourceId]
  if (!source) return state
  return {
    ...state,
    sources: {
      ...state.sources,
      [sourceId]: { ...source, data },
    },
  }
}

export const mapStyleReducer = (
  state: MapStyleState = null,
  action: MapStyleAction
): MapStyleState => {
  switch (action.type) {
    case SET_MAP_STYLE: {
      return action.payload
    }

    case 'TOGGLE_MAP_LAYER': {
      if (!state?.layers) return state
      const newMapStyle = deepClone(state)
      newMapStyle.layers
        .filter((layer: any) => action.payload.includes(layer.id))
        .forEach((layer: any) => {
          ensureLayerLayout(layer)
          layer.layout.visibility =
            layer.layout.visibility === 'none' ? 'visible' : 'none'
        })
      return newMapStyle
    }

    case 'TOGGLE_MAP_LAYERS_ON': {
      if (!state?.layers) return state
      const layerIds = getOutlinesAndSymbolLayers(action.payload)
      const newMapStyle = deepClone(state)
      newMapStyle.layers
        .filter((layer: any) => layerIds.includes(layer.id))
        .forEach((layer: any) => {
          ensureLayerLayout(layer)
          layer.layout.visibility = 'visible'
        })
      return newMapStyle
    }

    case 'TOGGLE_MAP_LAYERS_OFF': {
      if (!state?.layers) return state
      const layerIds = getOutlinesAndSymbolLayers(action.payload)
      const newMapStyle = deepClone(state)
      newMapStyle.layers
        .filter((layer: any) => layerIds.includes(layer.id))
        .forEach((layer: any) => {
          ensureLayerLayout(layer)
          layer.layout.visibility = 'none'
        })
      return newMapStyle
    }

    case TOGGLE_MAP_STYLE: {
      if (!state?.layers) return state
      const newStyle = (styleToggleObject as Record<string, any>)[
        action.payload
      ]
      if (!newStyle?.layers) {
        console.warn('Unknown map style toggle requested:', action.payload)
        return state
      }
      const layerArray: StyleLayer[] = []
      state.layers.forEach((currentLayer: any) => {
        let swapLayer = currentLayer
        newStyle.layers.forEach((newLayer: any) => {
          if (currentLayer.id === newLayer.id) {
            swapLayer = newLayer
          }
        })
        layerArray.push(swapLayer)
      })

      const newState = deepClone(state)
      newState.layers = layerArray
      newState.metadata = {
        ...(newState.metadata ?? {}),
        id: action.payload,
      }

      return newState
    }

    // --- Atomic actions (MAM-795) ---------------------------------------
    // Each case does a reference-preserving read-modify-write on a single slice
    // of the style tree. The whole point: no full-tree clone, so concurrent
    // dispatches can't clobber unrelated slices and MapLibre's style diff stays
    // cheap. These replaced the removed full-tree-replace updateMapStyle action.

    case MAP_STYLE_SELECTED_FEATURES_SET: {
      if (!state) return state
      return setSourceData(state, 'selected_features', {
        type: 'FeatureCollection',
        features: action.payload,
      })
    }

    case MAP_STYLE_CLICKED_FEATURE_SET: {
      if (!state) return state
      const { layer, sourceData } = action.payload
      let next = updateLayerById(state, 'clicked_feature', () => layer)
      if (sourceData) {
        next = setSourceData(next, sourceData.sourceId, sourceData.data)
      }
      return next
    }

    case MAP_STYLE_LAYER_FILTER_SET: {
      if (!state) return state
      const { layerId, filter } = action.payload
      return updateLayerById(state, layerId, layer => ({ ...layer, filter }))
    }

    case MAP_STYLE_LAYER_FILTERS_SET: {
      if (!state || !Array.isArray(state.layers)) return state
      // Per-layer filter set in a single reference-preserving pass. A null/
      // undefined filter removes the key (restore-to-baseline path).
      const byId = new Map(
        action.payload.map(entry => [entry.layerId, entry.filter])
      )
      let changed = false
      const layers = state.layers.map((layer: StyleLayer) => {
        if (!layer || !byId.has(layer.id ?? '')) return layer
        changed = true
        const filter = byId.get(layer.id ?? '')
        if (filter === null || filter === undefined) {
          const next = { ...layer }
          delete next.filter
          return next
        }
        return { ...layer, filter }
      })
      if (!changed) return state
      return { ...state, layers }
    }

    case MAP_STYLE_LAYER_VISIBILITY_SET: {
      if (!state) return state
      const { layerId, visibility } = action.payload
      return updateLayerById(state, layerId, layer => ({
        ...layer,
        layout: { ...(layer.layout ?? {}), visibility },
      }))
    }

    case MAP_STYLE_LAYERS_VISIBILITY_SET: {
      if (!state || !Array.isArray(state.layers)) return state
      const { layerIds, visibility } = action.payload
      const idSet = new Set(layerIds)
      let changed = false
      const layers = state.layers.map((layer: StyleLayer) => {
        if (!layer || !idSet.has(layer.id ?? '')) return layer
        if (layer.layout?.visibility === visibility) return layer
        changed = true
        return { ...layer, layout: { ...(layer.layout ?? {}), visibility } }
      })
      if (!changed) return state
      return { ...state, layers }
    }

    case MAP_STYLE_LAYERS_FILTER_AND_MODEL_SET: {
      if (!state || !Array.isArray(state.layers)) return state
      // Apply the same filter + grid filterModel to every target layer (or
      // remove both when `filter` is null) — the Data Table "apply filter to
      // map" path. metadata.filterModel is read back to restore the grid.
      const { layerIds, filter, filterModel } = action.payload
      const idSet = new Set(layerIds)
      let changed = false
      const layers = state.layers.map((layer: StyleLayer) => {
        if (!layer || !idSet.has(layer.id ?? '')) return layer
        changed = true
        if (filter === null || filter === undefined) {
          const next: StyleLayer = { ...layer }
          next.metadata = { ...(layer.metadata ?? {}) }
          delete next.filter
          delete next.metadata.filterModel
          return next
        }
        return {
          ...layer,
          filter,
          metadata: { ...(layer.metadata ?? {}), filterModel },
        }
      })
      if (!changed) return state
      return { ...state, layers }
    }

    case MAP_STYLE_LAYER_PAINT_SET: {
      if (!state) return state
      const { layerId, paint } = action.payload
      // Merge so callers can set individual paint props without clobbering the
      // rest of the layer's paint object.
      return updateLayerById(state, layerId, layer => ({
        ...layer,
        paint: { ...(layer.paint ?? {}), ...paint },
      }))
    }

    case MAP_STYLE_LAYER_TYPE_SET: {
      if (!state) return state
      const { layerId, layerType, paint } = action.payload
      return updateLayerById(state, layerId, layer => ({
        ...layer,
        type: layerType,
        ...(paint ? { paint } : {}),
      }))
    }

    case MAP_STYLE_SOURCE_DATA_SET: {
      if (!state) return state
      const { sourceId, data } = action.payload
      return setSourceData(state, sourceId, data)
    }

    case MAP_STYLE_SOURCE_SET: {
      if (!state) return state
      const { sourceId, source } = action.payload
      return {
        ...state,
        sources: { ...(state.sources ?? {}), [sourceId]: source },
      }
    }

    case MAP_STYLE_SOURCES_EMPTY: {
      if (!state?.sources) return state
      const emptyData = { type: 'FeatureCollection', features: [] }
      let changed = false
      const sources = { ...state.sources }
      action.payload.forEach((sourceId: string) => {
        const source = state.sources[sourceId]
        if (!source) return
        changed = true
        sources[sourceId] = { ...source, data: emptyData }
      })
      if (!changed) return state
      return { ...state, sources }
    }

    case MAP_STYLE_SOURCE_VERSION_BUMP: {
      if (!state?.sources || !Array.isArray(state.layers)) return state
      // Resolve the source backing `layerId`, then bump its version so MapLibre
      // refetches its tiles (legacy move-to-layer cache-bust behavior).
      const layer = state.layers.find(
        (styleLayer: StyleLayer) => styleLayer?.id === action.payload.layerId
      )
      const sourceId = layer?.source
      if (typeof sourceId !== 'string') return state
      const source = state.sources[sourceId]
      if (!source) return state
      const prev = source.version
      const version = typeof prev === 'number' ? prev + 1 : 1
      return {
        ...state,
        sources: { ...state.sources, [sourceId]: { ...source, version } },
      }
    }

    case MAP_STYLE_LAYERS_REPLACE: {
      if (!state) return state
      return { ...state, layers: action.payload }
    }

    case MAP_STYLE_BUST_LAYER_TILES: {
      if (!state) return state
      return bustLayerTilesSurgical(state, action.payload.layerId)
    }

    default:
      return state
  }
}
